import { useNavigate, useParams } from 'react-router'
import {
  useSupplierExternalBankAccounts,
  useSupplierExternalBankAccountMutations
} from '@klear/ui/hooks/use-supplier-external-bank-accounts'
import type { SupplierExternalBankAccount } from '@klear/supplier-external-bank-account'
import { Button } from '@klear/ui/components/button'
import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT
} from '@klear/constants'
import { DataTable } from '@klear/ui/components/data-table'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTableRowActions } from '@klear/ui/components/data-table-row-actions'
import { DropdownMenuItem } from '@klear/ui/components/dropdown-menu'
import { Badge } from '@klear/ui/components/badge'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { CreditCard } from 'lucide-react'
import { DeleteButton } from '@klear/ui/components/delete-button'
import noop from 'lodash/noop'
import toInteger from 'lodash/toInteger'

export default function BankAccounts() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { traderId, supplierId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const supplierIdNum = supplierId ? toInteger(supplierId) : undefined
  const { isLoading, supplierExternalBankAccounts } = useSupplierExternalBankAccounts(supplierIdNum)
  const { deleteSupplierExternalBankAccount } = useSupplierExternalBankAccountMutations()

  const handleEditBankAccount = (bankAccount: SupplierExternalBankAccount) => {
    navigate(`${urlPrefixApp}/supplier/${supplierId}/external-bank-account/${bankAccount.id}/edit`)
  }

  const handleAddBankAccount = () => {
    navigate(`${urlPrefixApp}/supplier/${supplierId}/external-bank-account/new`)
  }

  const columns: ColumnDef<SupplierExternalBankAccount>[] = [
    {
      header: 'Bank Name',
      accessorKey: 'bankName',
      cell: (info) => info.row.original.bankName
    },
    {
      header: 'Account Number',
      accessorKey: 'accountNumber',
      cell: (info) => info.row.original.accountNumber
    },
    {
      header: 'Routing Number',
      accessorKey: 'routingNumber',
      cell: (info) => info.row.original.routingNumber
    },
    {
      header: 'Account Holder Name',
      accessorKey: 'accountHolderName',
      cell: (info) => info.row.original.accountHolderName
    },
    {
      header: 'Account Holder Type',
      accessorKey: 'accountHolderType',
      cell: (info) => info.row.original.accountHolderType
    },
    {
      header: 'Supported Networks',
      accessorKey: 'supportedPaymentNetworks',
      cell: (info) => (
        <div className="flex flex-wrap gap-2 items-center justify-end">
          {info.row.original.supportedPaymentNetworks.map((network: string) => (
            <Badge key={network} variant="secondary" className="text-xs">
              {network}
            </Badge>
          ))}
        </div>
      )
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const handleDelete = async () => {
          if (row.original.id == null || supplierIdNum == null) return
          await deleteSupplierExternalBankAccount({ id: row.original.id, supplierId: supplierIdNum })
        }
        return (
          <div className="flex justify-end">
            <DataTableRowActions>
              {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
                <DropdownMenuItem onClick={() => handleEditBankAccount(row.original)}>Edit</DropdownMenuItem>
              )}
              {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_DELETE) && (
                <DeleteButton
                  resourceId={row.original.id ?? 0}
                  resourceName={row.original.bankName}
                  resourceType={PERMISSION_ENTITY_TREASURY}
                  deleteMutation={async () => noop()}
                  onSuccess={noop}
                  customOnClick={handleDelete}
                  wrapperComponent={DropdownMenuItem}
                >
                  Delete
                </DeleteButton>
              )}
            </DataTableRowActions>
          </div>
        )
      }
    }
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Bank Accounts
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">Manage supplier bank accounts for payments</p>
            </div>
            {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_ADD) && (
              <Button variant="outline" onClick={handleAddBankAccount}>
                Add Bank Account
              </Button>
            )}
          </div>

          {!isLoading && supplierExternalBankAccounts.length === 0 ? (
            <div className="rounded-lg border border-dashed p-10 text-center">
              <CreditCard className="mx-auto h-10 w-10 text-muted-foreground" />
              <h3 className="mt-4 text-sm font-semibold">No supplier bank accounts yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Add a bank account so you can pay this supplier.</p>
              {user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_ADD) && (
                <Button className="mt-4" onClick={handleAddBankAccount}>
                  Add Bank Account
                </Button>
              )}
            </div>
          ) : (
            <DataTable data={supplierExternalBankAccounts} columns={columns} loading={isLoading} />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
